import React, { useState, useEffect, useRef, useCallback } from "react";
import { updateBusiness, getBusinessById } from "../../api/businessApi";
import Select from "react-select";
import ActivityLogModal, {
  BUSINESS_FILTER_TYPES,
} from "../common/ActivityLogModal";

const BusinessDetails = ({
  business,
  onSave,
  showToast,
  businessTypes = [],
  loadingTypes = false,
}) => {
  const emptyForm = {
    companyName: "",
    tradingName: "",
    directorName: "",
    businessAddress: "",
    businessType: "",
    mobile: "",
    email: "",
    acquirerMId: "",
    pFeeCap: "",
    dFeeCap: "",
    version: 0,
    latLng: null,
    timezone: "",
  };

  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const addressInputRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const [originalAddress, setOriginalAddress] = useState("");
  const [showLogModal, setShowLogModal] = useState(false);
  // ---- Helper: populate form from a business object ----
  const loadBusinessIntoForm = useCallback((biz) => {
    if (!biz) return;
    const address = biz.businessAddress || biz.address || "";
    setOriginalAddress(address);
    setFormData({
      companyName: biz.companyName || biz.name || "",
      tradingName: biz.tradingName || "",
      timezone: biz.timezone || "",
      directorName: biz.directorName || "",
      businessAddress: address,
      businessType: biz.businessType || biz.category || "",
      mobile: biz.mobile || biz.phone || "",
      email: biz.email || "",
      acquirerMId: biz.acquirerMId || "",
      pFeeCap: biz.pFeeCap || "",
      dFeeCap: biz.dFeeCap || "",
      latLng: biz.latLng || null,
      version: biz.version || 0,
    });
  }, []);

  // ---- Init Google Places ----
  useEffect(() => {
    if (window.google && window.google.maps) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement("div"),
      );
    }
  }, []);

  // ---- Sync form when business prop changes ----
  useEffect(() => {
    loadBusinessIntoForm(business);
    setError("");
    setIsEditing(false);
  }, [business, loadBusinessIntoForm]);

  // ---- Address autocomplete ----
  const handleAddressInput = async (value) => {
    setFormData((prev) => ({ ...prev, businessAddress: value }));

    if (value.length > 2 && autocompleteServiceRef.current) {
      try {
        const request = {
          input: value,
          types: ["address"],
          componentRestrictions: { country: ["au", "nz"] },
        };

        autocompleteServiceRef.current.getPlacePredictions(
          request,
          (predictions, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              setAddressSuggestions(predictions);
              setShowSuggestions(true);
            } else {
              setAddressSuggestions([]);
              setShowSuggestions(false);
            }
          },
        );
      } catch (err) {
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleAddressSelect = (suggestion) => {
    setShowSuggestions(false);
    setFormData((prev) => ({
      ...prev,
      businessAddress: suggestion.description,
    }));

    if (placesServiceRef.current) {
      const request = {
        placeId: suggestion.place_id,
        fields: ["geometry", "formatted_address"],
      };

      placesServiceRef.current.getDetails(request, (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place &&
          place.geometry
        ) {
          setFormData((prev) => ({
            ...prev,
            businessAddress: place.formatted_address || prev.businessAddress,
            latLng: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            },
          }));
        }
      });
    }
  };

  const handleAddressBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleAddressFocus = () => {
    if (formData.businessAddress && formData.businessAddress.length > 2) {
      handleAddressInput(formData.businessAddress);
    }
  };

  // ---- Submit ----
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const currentAddress = formData.businessAddress || "";
      const addressChanged = originalAddress !== currentAddress;

      const payload = {
        companyName: formData.companyName,
        tradingName: formData.tradingName || formData.companyName,
        directorName: formData.directorName,
        businessAddress: formData.businessAddress,
        businessType: formData.businessType,
        mobile: formData.mobile,
        email: formData.email,
        acquirerMId: formData.acquirerMId,
        pFeeCap: formData.pFeeCap || "0",
        dFeeCap: formData.dFeeCap || "0",
        version: formData.version,
        latLng: addressChanged ? formData.latLng : null,
      };

      const response = await updateBusiness(
        business.id || business.businessGuid,
        payload,
      );

      const updatedBusiness = response.data || response;

      setOriginalAddress(formData.businessAddress);
      setIsEditing(false);

      if (onSave) onSave(updatedBusiness);
      showToast("Business updated successfully", "success");
    } catch (err) {
      // ---- 409 Conflict: refetch and repopulate ----
      if (err.statusCode === 409) {
        const conflictType = err.name || "Conflict";
        const conflictMessage =
          err.details || "Conflict detected. Please refresh and try again.";
        showToast(`${conflictType}: ${conflictMessage}.`, "warning", true);

        try {
          const fresh = await getBusinessById(
            business.id || business.businessGuid,
          );
          loadBusinessIntoForm(fresh);
          if (onSave) onSave(fresh);
        } catch (refetchErr) {
          showToast("Failed to reload latest business details", "error");
        }

        setIsEditing(false);
      } else {
        const errorMsg =
          err.response?.data?.message ||
          err.message ||
          "Failed to save business. Please try again.";
        setError(errorMsg);
        showToast(errorMsg, "danger");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    setIsEditing(false);
    loadBusinessIntoForm(business);
    setError("");
    setShowSuggestions(false);
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Merchant ID */}
          <div className="row align-items-end">
            <div className="col-md-6 mb-3">
              <label className="form-label">Merchant ID *</label>
              <input
                type="text"
                className="form-control"
                value={formData.acquirerMId}
                onChange={(e) => {
                  const cleaned = e.target.value
                    .replace(/[^a-zA-Z0-9]/g, "")
                    .toUpperCase();
                  setFormData((prev) => ({ ...prev, acquirerMId: cleaned }));
                }}
                onKeyDown={(e) => {
                  const allowed =
                    /[a-zA-Z0-9]/.test(e.key) ||
                    [
                      "Backspace",
                      "Delete",
                      "ArrowLeft",
                      "ArrowRight",
                      "Tab",
                      "Home",
                      "End",
                    ].includes(e.key) ||
                    e.ctrlKey ||
                    e.metaKey;
                  if (!allowed) e.preventDefault();
                }}
                minLength={5}
                maxLength={25}
                required
                disabled={!isEditing || loading}
                pattern="[A-Z0-9]+"
                title="Only uppercase letters and numbers are allowed (5–25 characters)"
              />
            </div>

            <div className="col-md-6 mb-3">
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setShowLogModal(true)}
                  disabled={!business?.id && !business?.businessGuid}
                >
                  <i className="bi bi-clock-history me-1"></i>
                  Activity Log
                </button>
              </div>

              <ActivityLogModal
                show={showLogModal}
                onClose={() => setShowLogModal(false)}
                entityType="Business"
                entityId={business?.id || business?.businessGuid}
                title={`Activity Log — ${formData.companyName || "Business"}`}
                filterTypes={BUSINESS_FILTER_TYPES}
              />
            </div>
          </div>

          {/* Company Name */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Company Name *</label>
            <input
              type="text"
              className="form-control"
              value={formData.companyName}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  companyName: e.target.value,
                }))
              }
              disabled={!isEditing || loading}
              required
            />
          </div>

          {/* Trading Name */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Trading Name *</label>
            <input
              type="text"
              className="form-control"
              value={formData.tradingName}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  tradingName: e.target.value,
                }))
              }
              disabled={!isEditing || loading}
              required
            />
          </div>

          {/* Director Name */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Director Name *</label>
            <input
              type="text"
              className="form-control"
              value={formData.directorName}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  directorName: e.target.value,
                }))
              }
              disabled={!isEditing || loading}
              required
            />
          </div>

          {/* Business Address */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Business Address *</label>
            <div className="position-relative">
              <input
                ref={addressInputRef}
                type="text"
                className="form-control"
                value={formData.businessAddress}
                onChange={(e) => handleAddressInput(e.target.value)}
                onFocus={handleAddressFocus}
                onBlur={handleAddressBlur}
                disabled={!isEditing || loading}
                required
                placeholder="Start typing your business address..."
              />
              {showSuggestions && addressSuggestions.length > 0 && (
                <ul
                  className="list-group position-absolute w-100 mt-1 shadow-lg"
                  style={{ zIndex: 1000 }}
                >
                  {addressSuggestions.map((suggestion) => (
                    <li
                      key={suggestion.place_id}
                      className="list-group-item list-group-item-action"
                      style={{ cursor: "pointer" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAddressSelect(suggestion);
                      }}
                    >
                      <strong>
                        {suggestion.structured_formatting?.main_text ||
                          suggestion.description}
                      </strong>
                      <br />
                      <small className="text-muted">
                        {suggestion.structured_formatting?.secondary_text || ""}
                      </small>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* {formData.latLng && formData.latLng.lat !== 0 && (
              <small className="text-muted mt-1 d-block">
                <i className="bi bi-geo-alt me-1"></i>
                Latitude: {formData.latLng.lat.toFixed(6)}, Longitude:{" "}
                {formData.latLng.lng.toFixed(6)}
              </small>
            )} */}
          </div>

          {/* Mobile */}
          <div className="col-md-4 mb-3">
            <label className="form-label">Mobile Number *</label>
            <input
              type="text"
              className="form-control"
              value={formData.mobile}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, mobile: e.target.value }))
              }
              disabled={!isEditing || loading}
            />
          </div>

          {/* Email */}
          <div className="col-md-4 mb-3">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              disabled={!isEditing || loading}
            />
          </div>

          {/* Business Type */}
          <div className="col-md-4 mb-3">
            <label className="form-label">Business Type</label>
            <Select
              options={businessTypes.map((type) => ({
                value: type.id || type.value,
                label: type.typeDesc || type.label || type.name,
              }))}
              value={
                formData.businessType
                  ? {
                      value: formData.businessType,
                      label:
                        businessTypes.find(
                          (t) => (t.id || t.value) === formData.businessType,
                        )?.typeDesc ||
                        businessTypes.find(
                          (t) => (t.id || t.value) === formData.businessType,
                        )?.label ||
                        formData.businessType,
                    }
                  : null
              }
              onChange={(selected) =>
                setFormData((prev) => ({
                  ...prev,
                  businessType: selected ? selected.value : "",
                }))
              }
              isLoading={loadingTypes}
              placeholder="Select Business Type"
              isClearable
              isSearchable
              isDisabled={!isEditing || loading || loadingTypes}
              className="react-select-container"
              classNamePrefix="react-select"
            />
            {loadingTypes && (
              <small className="text-muted">Loading types...</small>
            )}
          </div>

          {/* FeeCap % */}
          <div className="col-md-4 mb-3">
            <label className="form-label">FeeCap(%)</label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              value={formData.pFeeCap}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, pFeeCap: e.target.value }))
              }
              disabled={!isEditing || loading}
            />
          </div>

          {/* FeeCap $ */}
          <div className="col-md-4 mb-3">
            <label className="form-label">FeeCap($)</label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              value={formData.dFeeCap}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, dFeeCap: e.target.value }))
              }
              disabled={!isEditing || loading}
            />
          </div>

          {/* Timezone */}
          <div className="col-md-4 mb-3">
            <label className="form-label">Time zone</label>
            <input
              type="text"
              className="form-control"
              value={formData.timezone}
              disabled
              style={{ backgroundColor: "#e9ecef" }}
            />
          </div>
        </div>

        {error && (
          <div className="alert alert-danger py-2 d-flex align-items-center gap-2">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
          </div>
        )}
      </form>

      <div className="d-flex justify-content-end mb-3">
        {!isEditing ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleEdit}
          >
            <i className="bi bi-pencil me-1"></i>Edit Details
          </button>
        ) : (
          <div>
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessDetails;
